import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Pencil, Trash2, Plus, Users, ArrowLeft, Upload } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../../components/ui/dialog";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";

// Aceita "Nome, 5511999998888" ou "Nome;5511999998888", uma linha por contato.
const interpretarLinhas = (texto) =>
	texto
		.split("\n")
		.map((linha) => linha.trim())
		.filter(Boolean)
		.map((linha) => {
			const partes = linha.split(/[,;\t]/).map((p) => p.trim());
			const numero = (partes[1] || partes[0] || "").replace(/\D/g, "");
			const nome = partes.length > 1 ? partes[0] : numero;
			return { name: nome, number: numero };
		})
		.filter((item) => item.number.length >= 10);

const ContactLists = () => {
	const [lists, setLists] = useState([]);
	const [loading, setLoading] = useState(false);
	const [aberta, setAberta] = useState(null);
	const [items, setItems] = useState([]);

	const [modalOpen, setModalOpen] = useState(false);
	const [nome, setNome] = useState("");
	const [editando, setEditando] = useState(null);

	const [importOpen, setImportOpen] = useState(false);
	const [textoImport, setTextoImport] = useState("");

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [aExcluir, setAExcluir] = useState(null);

	const carregarListas = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/contact-lists");
			setLists(data);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	}, []);

	const carregarItens = useCallback(async (listId) => {
		try {
			const { data } = await api.get(`/contact-lists/${listId}/items`);
			setItems(data);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		carregarListas();
	}, [carregarListas]);

	useEffect(() => {
		if (aberta) carregarItens(aberta.id);
	}, [aberta, carregarItens]);

	const salvarLista = async () => {
		if (!nome.trim()) return;
		try {
			if (editando) {
				await api.put(`/contact-lists/${editando.id}`, { name: nome });
			} else {
				await api.post("/contact-lists", { name: nome });
			}
			toast.success("Lista salva.");
			setModalOpen(false);
			setNome("");
			setEditando(null);
			carregarListas();
		} catch (err) {
			toastError(err);
		}
	};

	const importar = async () => {
		const contatos = interpretarLinhas(textoImport);

		if (contatos.length === 0) {
			toast.error("Nenhum número válido encontrado.");
			return;
		}

		try {
			const { data } = await api.post(`/contact-lists/${aberta.id}/items`, {
				contacts: contatos,
			});
			toast.success(
				`${data.created} contato(s) adicionado(s). Repetidos foram ignorados.`
			);
			setImportOpen(false);
			setTextoImport("");
			carregarItens(aberta.id);
			carregarListas();
		} catch (err) {
			toastError(err);
		}
	};

	const excluir = async () => {
		try {
			if (aExcluir.tipo === "lista") {
				await api.delete(`/contact-lists/${aExcluir.id}`);
				if (aberta?.id === aExcluir.id) setAberta(null);
				carregarListas();
			} else {
				await api.delete(`/contact-list-items/${aExcluir.id}`);
				carregarItens(aberta.id);
				carregarListas();
			}
			toast.success("Excluído.");
		} catch (err) {
			toastError(err);
		}
		setAExcluir(null);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={aExcluir?.titulo}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setAExcluir(null);
				}}
				onConfirm={excluir}
			>
				{aExcluir?.tipo === "lista"
					? "A lista some com todos os contatos que estão nela. Os contatos continuam existindo no sistema; campanhas já criadas com esta lista não mudam."
					: "O contato sai desta lista e não entra nas próximas campanhas feitas com ela. Ele continua no sistema."}
			</ConfirmationModal>

			{/* Nova lista / renomear */}
			<Dialog open={modalOpen} onOpenChange={setModalOpen}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>
							{editando ? "Renomear lista" : "Nova lista"}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-1.5">
						<Label htmlFor="nomeLista">Nome</Label>
						<Input
							id="nomeLista"
							value={nome}
							onChange={(e) => setNome(e.target.value)}
							placeholder="Clientes VIP"
							autoFocus
							onKeyDown={(e) => e.key === "Enter" && salvarLista()}
						/>
						<p className="text-xs text-muted-foreground">
							Um nome que você reconheça depois, na hora de montar a campanha.
							Exemplo: “Clientes VIP”, “Inadimplentes”.
						</p>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setModalOpen(false)}>
							Cancelar
						</Button>
						<Button onClick={salvarLista}>
							{editando ? "Salvar nome" : "Criar lista vazia"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Importação */}
			<Dialog open={importOpen} onOpenChange={setImportOpen}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Adicionar contatos a esta lista</DialogTitle>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Colar nomes aqui não manda mensagem nenhuma. Eles só ficam
							guardados nesta lista.
						</p>
					</DialogHeader>
					<div className="space-y-1.5">
						<Label htmlFor="textoImport">Um contato por linha</Label>
						<Textarea
							id="textoImport"
							rows={10}
							value={textoImport}
							onChange={(e) => setTextoImport(e.target.value)}
							placeholder={"Maria Silva, 5511999998888\nJoão Souza, 5511988887777"}
							className="font-mono text-xs"
						/>
						<p className="text-xs text-muted-foreground">
							Separe nome e número por vírgula ou ponto e vírgula. O número
							precisa ter DDD e o 55 do Brasil na frente. Números repetidos são
							ignorados automaticamente. Linhas sem número válido são puladas em
							silêncio — por isso o botão mostra quantas serão aproveitadas.
						</p>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setImportOpen(false)}>
							Cancelar
						</Button>
						<Button onClick={importar}>
							{interpretarLinhas(textoImport).length
								? `Adicionar ${interpretarLinhas(textoImport).length} contato(s) à lista`
								: "Adicionar à lista"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<MainHeader>
				{aberta ? (
					<div className="flex min-w-0 items-center gap-2">
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8"
							aria-label="Voltar às listas"
							onClick={() => setAberta(null)}
						>
							<ArrowLeft className="h-4 w-4" />
						</Button>
						<Title>{aberta.name}</Title>
					</div>
				) : (
					<Title>Listas de contatos</Title>
				)}
				<MainHeaderButtonsWrapper>
					{aberta ? (
						<Button onClick={() => setImportOpen(true)}>
							<Upload className="h-4 w-4" />
							Adicionar contatos
						</Button>
					) : (
						<Button
							onClick={() => {
								setEditando(null);
								setNome("");
								setModalOpen(true);
							}}
						>
							<Plus className="h-4 w-4" />
							Nova lista
						</Button>
					)}
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				{!aberta && (
					<>
						{/* O alívio importa tanto quanto o aviso: esta tela não envia. */}
						<div className="mb-4 rounded-xl border bg-muted p-3 text-xs leading-relaxed">
							<p className="font-medium">
								Nada é enviado a partir daqui.
							</p>
							<p className="mt-1.5 text-muted-foreground">
								Grupos de contatos prontos para usar numa campanha.
							</p>
						</div>

						{!loading && lists.length === 0 && (
							<div className="rounded-xl border bg-card py-16 text-center">
								<p className="text-sm font-medium">Nenhuma lista criada</p>
								<p className="mt-1 text-xs text-muted-foreground">
									Crie uma lista com os contatos que costumam receber o mesmo
									aviso. Depois é só escolher ela na campanha.
								</p>
							</div>
						)}
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{lists.map((list) => (
								<div
									key={list.id}
									className="flex items-center gap-3 rounded-xl border bg-card p-4"
								>
									<button
										type="button"
										onClick={() => setAberta(list)}
										className="flex min-w-0 flex-1 items-center gap-3 text-left"
									>
										<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
											<Users className="h-4 w-4" />
										</span>
										<span className="min-w-0">
											<span className="block truncate font-medium">
												{list.name}
											</span>
											<span className="block text-xs text-muted-foreground">
												{list.items?.length
													? `${list.items.length} contato(s) receberiam uma campanha feita com esta lista`
													: "Vazia — ninguém receberia nada"}
											</span>
										</span>
									</button>
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 shrink-0"
										aria-label="Renomear lista"
										onClick={() => {
											setEditando(list);
											setNome(list.name);
											setModalOpen(true);
										}}
									>
										<Pencil className="h-3.5 w-3.5" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 shrink-0"
										aria-label="Excluir lista"
										onClick={() => {
											setAExcluir({
												tipo: "lista",
												id: list.id,
												titulo: `Excluir ${list.name}?`,
											});
											setConfirmOpen(true);
										}}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</div>
							))}
						</div>
					</>
				)}

				{aberta && (
					<>
					<p className="mb-3 text-xs leading-relaxed text-muted-foreground">
						{items.length
							? `${items.length} pessoa(s) nesta lista. Elas só recebem alguma coisa se você criar uma campanha e escolher a lista “${aberta.name}”.`
							: "Lista vazia. Ninguém recebe nada enquanto ela estiver assim."}
					</p>
					<div className="overflow-x-auto rounded-xl border bg-card">
						<Table>
							<TableHeader className="sticky top-0 z-10 bg-card">
								<TableRow>
									<TableHead>Nome</TableHead>
									<TableHead>Número</TableHead>
									<TableHead className="text-center">Ações</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{items.map((item) => (
									<TableRow key={item.id}>
										<TableCell className="font-medium">{item.name}</TableCell>
										<TableCell className="tabular-nums">
											{item.number}
										</TableCell>
										<TableCell className="text-center">
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												aria-label="Remover contato"
												onClick={() => {
													setAExcluir({
														tipo: "item",
														id: item.id,
														titulo: `Remover ${item.name} da lista?`,
													});
													setConfirmOpen(true);
												}}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</TableCell>
									</TableRow>
								))}
								{items.length === 0 && (
									<TableRow>
										<TableCell
											colSpan={3}
											className="py-10 text-center text-sm text-muted-foreground"
										>
											Lista vazia. Use “Adicionar contatos”.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					</>
				)}
			</div>
		</MainContainer>
	);
};

export default ContactLists;
